// Lightweight client-side geocode helper with debounce support.
// Uses public Nominatim endpoint; for production heavy use consider self-hosting + caching.

let pending = null;
let lastController = null;

export function geocodeAddressClient(query, { signal, limit = 1, notableOnly = false } = {}) {
  if(!query || query.trim().length < 3) return Promise.resolve(null);
  // Abort previous fetch if still running
  if(lastController) lastController.abort();
  const ctrl = new AbortController();
  lastController = ctrl;
  const params = new URLSearchParams({
    format: 'jsonv2',
    limit: String(limit),
    // addressdetails=1 so we can reconstruct structured address (postcode city street house_number)
    addressdetails: '1',
    countrycodes: 'hu',
    bounded: '1',
    viewbox: '16.1,48.7,22.9,45.7',
    q: query
  });
  const url = `https://nominatim.openstreetmap.org/search?${params.toString()}`;
  if(typeof window !== 'undefined') console.log('[geocode] query=', query, 'limit=', limit);
  return fetch(url, { signal: ctrl.signal, headers: { 'Accept':'application/json', 'User-Agent':'smartbin-ui/1.0' } })
    .then(r => r.ok ? r.json() : (console.warn('Geocode HTTP status', r.status), []))
    .then(arr => {
  if(typeof window !== 'undefined') console.log('[geocode] raw count=', Array.isArray(arr)? arr.length : 'n/a');
  if(!Array.isArray(arr) || !arr.length) return null;
      // Pre-calculate notable heuristic criteria so we can both filter (when notableOnly) and annotate each result.
      const classWhitelist = new Set(['tourism','amenity','leisure','natural','historic','aeroway','man_made','waterway','shop','building','railway']);
      const importanceMin = 0.5;
      const mapped = arr.map(r => {
        const lat = parseFloat(r.lat), lon = parseFloat(r.lon);
        if(isNaN(lat) || isNaN(lon)) return null;
        const a = r.address || {};
        // Prefer city/town/village order
        const city = a.city || a.town || a.village || a.municipality || '';
        const street = a.road || a.pedestrian || a.cycleway || a.path || a.street || '';
        const number = a.house_number || '';
        const postcode = a.postcode || '';
        const isNotable = (r.importance && r.importance >= importanceMin) || classWhitelist.has(r.class);
        return {
          latitude: lat,
            longitude: lon,
            display: r.display_name,
            components: { postcode, city, street, number },
            importance: r.importance,
            class: r.class,
            type: r.type,
            _notable: !!isNotable,
            raw: r
        };
      }).filter(Boolean);
      if(!mapped.length) return null;
      let filtered = mapped;
      if(notableOnly){
        // notableOnly: szűrés az előbb számított _notable attribútum alapján
        filtered = mapped.filter(m => m._notable);
        if(typeof window !== 'undefined') console.log('[geocode] notable filtered', filtered.length, 'of', mapped.length);
        if(!filtered.length) return null; // nincs nevezetes találat → vissza null, hogy ne jelenjen meg tág lista
      }
  if(typeof window !== 'undefined') console.log('[geocode] mapped results=', (filtered||mapped).length);
  return limit === 1 ? filtered[0] : filtered;
    })
    .catch(e => {
      if(e.name === 'AbortError') return null; // silent
      console.warn('Geocode fetch error', e);
      return null;
    });
}

// Debounce wrapper (simple trailing debounce)
export function debounceGeocode(fn, delay = 500){
  let timer = null;
  return (...args) => {
    return new Promise(resolve => {
      if(timer) clearTimeout(timer);
      timer = setTimeout(async () => {
        const res = await fn(...args);
        resolve(res);
      }, delay);
    });
  };
}

export const debouncedGeocode = debounceGeocode((q)=> geocodeAddressClient(q, { limit:1 }), 600);
export const debouncedGeocodeMulti = debounceGeocode((q)=> geocodeAddressClient(q, { limit:5 }), 650);
// Notable-only variants (landmarks / higher importance)
export const debouncedGeocodeNotable = debounceGeocode((q)=> geocodeAddressClient(q, { limit:1, notableOnly:true }), 600);
export const debouncedGeocodeMultiNotable = debounceGeocode((q)=> geocodeAddressClient(q, { limit:5, notableOnly:true }), 650);

// Smart strategy: próbálj notable találatokat. Ha nincs, vagy a query tipikusan utca + házszám mintájú (pl. "Palánkai u 33"), akkor bővíts teljes találatra.
async function smartGeocodeMulti(query){
  if(!query || query.trim().length < 3) return null;
  const q = query.trim();
  const streetNumberPattern = /( u\.?| utca| u\b| street| str\.?| tér| ter| road| rd\.?| avenue| ave\.?| fasor| körút| krt\.?| krt)/i;
  const hasNumber = /\d/.test(q);
  const looksLikeAddress = hasNumber && streetNumberPattern.test(q);
  // 1. notable próbálkozás
  let res = await geocodeAddressClient(q, { limit:5, notableOnly:true });
  if(res && res.length) return res;
  // 2. ha nincs notable VAGY kifejezetten cím-szerű a lekérdezés, próbáljuk teljes listát
  res = await geocodeAddressClient(q, { limit:8, notableOnly:false });
  return res;
}

export const debouncedGeocodeMultiSmart = debounceGeocode((q)=> smartGeocodeMulti(q), 650);
